/**
 * useInvoices Hook
 * Fetches invoices with filtering and pagination support
 *
 * @author SAUBHAGYA Development Team
 * @since US-INV-001
 */

import { useQuery } from '@tanstack/react-query';
import type { InvoicesResponse, InvoicesQueryParams, UseInvoicesReturn } from '../types/invoice.types';
import { getInvoices } from '../api/invoiceApi';

// ============================================
// QUERY KEY FACTORY
// ============================================

const invoicesQueryKeys = {
  all: ['invoices'] as const,
  lists: () => [...invoicesQueryKeys.all, 'list'] as const,
  list: (params: InvoicesQueryParams) =>
    [...invoicesQueryKeys.lists(), params] as const,
};

// ============================================
// API CALL
// ============================================

/**
 * Fetch invoices from API
 */
async function fetchInvoices(params: InvoicesQueryParams): Promise<InvoicesResponse> {
  return await getInvoices(params);
}

// ============================================
// HOOK
// ============================================

/**
 * Hook for fetching invoices with filters
 * Uses React Query for caching and background updates
 *
 * @param params Query parameters for filtering and pagination
 * @returns Invoice list data and loading states
 *
 * @example
 * ```tsx
 * const { data, isLoading, isError, error, refetch } = useInvoices({
 *   page: 0,
 *   size: 20,
 *   searchTerm: 'INV-001',
 *   statuses: ['PAID'],
 *   dateFrom: '2024-01-01',
 *   dateTo: '2024-12-31'
 * });
 * ```
 */
export function useInvoices(params: InvoicesQueryParams): UseInvoicesReturn {
  const {
    data,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: invoicesQueryKeys.list(params),
    queryFn: () => fetchInvoices(params),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    refetchOnWindowFocus: true,
  });

  return {
    data,
    isLoading,
    isError,
    error: error as Error | null,
    refetch,
  };
}

export default useInvoices;
